import React, { useEffect, useMemo, useState } from 'react';
import { ResumeAnalysis, AnalysisError, DataValidationError, Discrepancy, EmploymentGap, JobHoppingPattern, GrammarError, Improvement, RewriteOptimizationSuggestion } from '../../../shared/types';
import './ResumeAnalysisResults.css';

const LEADING_LIST_MARKER_PATTERN = /^(\s*(?:[•●◦▪▫◉*\-–—]|\d+[.)]|[A-Za-z][.)])\s+)(.*)$/;

type SelectableSuggestionSection =
  | 'atsErrors'
  | 'dataValidationErrors'
  | 'grammarErrors'
  | 'professionalImprovements'
  | 'fieldSpecificSuggestions'
  | 'contextualImprovements'
  | 'otherSuggestions'
  | 'rewriteOptimizationSuggestions';

interface SelectableSuggestionMeta {
  id: string;
  applicable: boolean;
  label: string;
  reason?: string;
}

type ResumeGenerationSourceFormat = 'docx' | 'pdf' | 'other';

interface ResumeAnalysisResultsProps {
  analysis: ResumeAnalysis;
  isLoading?: boolean;
  error?: string | null;
  onGenerateResume?: (selectedSuggestionIds: string[]) => void;
  onGenerateResumeAi?: (selectedSuggestionIds: string[]) => void;
  isGeneratingResume?: boolean;
  isGeneratingResumeAi?: boolean;
  sourceFormat?: ResumeGenerationSourceFormat;
}

const buildSafeReplacementTarget = (
  source?: string | null,
  target?: string | null,
): string | null => {
  if (!target) {
    return target ?? null;
  }

  if (!source) {
    return target;
  }

  const sourceMatch = source.match(LEADING_LIST_MARKER_PATTERN);
  const targetMatch = target.match(LEADING_LIST_MARKER_PATTERN);

  if (sourceMatch && targetMatch) {
    return `${sourceMatch[1]}${targetMatch[2]}`;
  }

  if (sourceMatch) {
    return `${sourceMatch[1]}${target.trimStart()}`;
  }

  if (targetMatch) {
    const sourceIndent = source.match(/^\s*/)?.[0] ?? '';
    return `${sourceIndent}${targetMatch[2]}`;
  }

  return target;
};

const hasAutoApplicableReplacement = (
  source?: string | null,
  target?: string | null,
): boolean => {
  const safeTarget = buildSafeReplacementTarget(source, target);
  return Boolean(source && safeTarget && source !== safeTarget);
};

const buildAtsErrorLabel = (error: AnalysisError): string => {
  if (error.section) {
    return `${error.section} ATS issue`;
  }

  if (error.type) {
    return `${error.type.replace('ats_', '').replace(/_/g, ' ')} issue`;
  }

  return 'ATS issue';
};

export const ResumeAnalysisResults: React.FC<ResumeAnalysisResultsProps> = ({
  analysis,
  isLoading = false,
  error = null,
  onGenerateResume,
  onGenerateResumeAi,
  isGeneratingResume = false,
  isGeneratingResumeAi = false,
  sourceFormat = 'docx',
}) => {
  // Use raw data without filtering - show all analysis results
  const filteredAtsErrors = analysis.atsErrors || [];
  const filteredDataValidationErrors = analysis.dataValidationErrors || [];
  const filteredDiscrepancies = analysis.discrepancies || [];
  const filteredGrammarErrors = analysis.grammarErrors || [];
  const filteredProfessionalImprovements = analysis.professionalImprovements || [];
  const filteredFieldSpecificSuggestions = analysis.fieldSpecificSuggestions || [];
  const filteredContextualImprovements = analysis.contextualImprovements || [];
  const filteredOtherSuggestions = analysis.otherSuggestions || [];
  const filteredRewriteOptimizationSuggestions = analysis.rewriteOptimizationSuggestions || [];

  const selectableSuggestions = useMemo<SelectableSuggestionMeta[]>(() => {
    const suggestions: SelectableSuggestionMeta[] = [];

    const register = (
      section: SelectableSuggestionSection,
      index: number,
      applicable: boolean,
      label: string,
      reason?: string,
    ) => {
      suggestions.push({
        id: `${section}:${index}`,
        applicable,
        label,
        reason,
      });
    };

    filteredAtsErrors.forEach((error, index) => {
      register(
        'atsErrors',
        index,
        false,
        buildAtsErrorLabel(error),
        'ATS findings can be selected for tracking, but they do not include exact source and replacement text for safe automatic application.',
      );
    });

    filteredDataValidationErrors.forEach((error, index) => {
      register(
        'dataValidationErrors',
        index,
        hasAutoApplicableReplacement(error.currentValue, error.suggestedValue),
        error.field || 'Data validation fix',
        'Needs a specific current and suggested value pair to be applied automatically.',
      );
    });

    filteredGrammarErrors.forEach((error, index) => {
      register(
        'grammarErrors',
        index,
        hasAutoApplicableReplacement(error.original, error.corrected),
        error.type ? `${error.type} correction` : 'Grammar correction',
        'Needs a specific original and corrected text pair to be applied automatically.',
      );
    });

    const registerImprovementGroup = (
      section: Exclude<SelectableSuggestionSection, 'atsErrors' | 'dataValidationErrors' | 'grammarErrors' | 'rewriteOptimizationSuggestions'>,
      items: Improvement[],
      fallbackLabel: string,
    ) => {
      items.forEach((item, index) => {
        register(
          section,
          index,
          hasAutoApplicableReplacement(item.before, item.after),
          item.category || fallbackLabel,
          'This suggestion is informational only because it does not include a concrete before/after replacement.',
        );
      });
    };

    registerImprovementGroup('professionalImprovements', filteredProfessionalImprovements, 'Professional improvement');
    registerImprovementGroup('fieldSpecificSuggestions', filteredFieldSpecificSuggestions, 'Field-specific suggestion');
    registerImprovementGroup('contextualImprovements', filteredContextualImprovements, 'Contextual improvement');
    registerImprovementGroup('otherSuggestions', filteredOtherSuggestions, 'Other suggestion');

    filteredRewriteOptimizationSuggestions.forEach((suggestion, index) => {
      register(
        'rewriteOptimizationSuggestions',
        index,
        hasAutoApplicableReplacement(suggestion.before, suggestion.after),
        suggestion.category || 'Rewrite suggestion',
        'This suggestion is informational only because it does not include a concrete before/after replacement.',
      );
    });

    return suggestions;
  }, [
    filteredAtsErrors,
    filteredContextualImprovements,
    filteredDataValidationErrors,
    filteredFieldSpecificSuggestions,
    filteredGrammarErrors,
    filteredOtherSuggestions,
    filteredProfessionalImprovements,
    filteredRewriteOptimizationSuggestions,
  ]);

  const allSelectableSuggestionIds = useMemo(
    () => selectableSuggestions.map((suggestion) => suggestion.id),
    [selectableSuggestions],
  );

  const selectableSuggestionMeta = useMemo(
    () => new Map(selectableSuggestions.map((suggestion) => [suggestion.id, suggestion])),
    [selectableSuggestions],
  );

  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState<string[]>([]);
  const [generationInlineError, setGenerationInlineError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedSuggestionIds([]);
    setGenerationInlineError(null);
  }, [analysis.createdAt, analysis.resumeId, analysis.updatedAt]);

  const selectedSuggestionCount = selectedSuggestionIds.length;
  const selectedApplicableSuggestionIds = useMemo(
    () => selectedSuggestionIds.filter((suggestionId) => selectableSuggestionMeta.get(suggestionId)?.applicable),
    [selectedSuggestionIds, selectableSuggestionMeta],
  );
  const selectedReviewOnlyCount = selectedSuggestionCount - selectedApplicableSuggestionIds.length;
  const exactApplyRequiresConcreteReplacements = sourceFormat === 'docx';
  const combinedGenerationDisabledReason = !selectedSuggestionCount
    ? 'Select at least one suggestion to generate a new resume.'
    : null;

  const toggleSuggestionSelection = (suggestionId: string) => {
    if (!selectableSuggestionMeta.has(suggestionId)) {
      return;
    }

    setSelectedSuggestionIds((currentSelection) => (
      currentSelection.includes(suggestionId)
        ? currentSelection.filter((currentId) => currentId !== suggestionId)
        : [...currentSelection, suggestionId]
    ));
  };

  const handleSelectAllSuggestions = () => {
    setSelectedSuggestionIds(allSelectableSuggestionIds);
  };

  const handleClearSelectedSuggestions = () => {
    setSelectedSuggestionIds([]);
    setGenerationInlineError(null);
  };

  const getSelectionMeta = (section: SelectableSuggestionSection, index: number): SelectableSuggestionMeta | null =>
    selectableSuggestionMeta.get(`${section}:${index}`) || null;

  const renderSuggestionSelectionControl = (section: SelectableSuggestionSection, index: number) => {
    const meta = getSelectionMeta(section, index);
    if (!meta) {
      return null;
    }

    const isSelected = selectedSuggestionIds.includes(meta.id);
    const handleToggle = () => {
      setGenerationInlineError(null);
      toggleSuggestionSelection(meta.id);
    };
    const helperText = meta.applicable
      ? (isSelected ? 'Selected. This change will be applied to the generated resume.' : 'Select this to apply the change to the generated resume.')
      : exactApplyRequiresConcreteReplacements
        ? (
          isSelected
            ? 'Selected. AI/PDF generation can use this suggestion even though the exact DOCX patcher may skip it.'
            : 'Select this to include it in AI/PDF generation. The exact DOCX patcher only auto-applies concrete Before/After changes.'
        )
        : (
          isSelected
            ? 'Selected. This suggestion will guide the generated PDF resume.'
            : 'Select this to apply it through AI-guided PDF generation.'
        );
    const actionLabel = isSelected
      ? 'Selected'
      : (meta.applicable || !exactApplyRequiresConcreteReplacements ? 'Apply To Resume' : 'Use In AI/PDF');

    return (
      <div className="analysis-selection-control">
        <div className="analysis-selection-copy">
          <span className="analysis-selection-label">{meta.label}</span>
          <span className="analysis-selection-helper">{helperText}</span>
        </div>
        <div className="analysis-selection-actions">
          {!meta.applicable && exactApplyRequiresConcreteReplacements && (
            <span className="analysis-selection-status" title={meta.reason || undefined}>
              AI / PDF
            </span>
          )}
          <button
            type="button"
            className={`analysis-selection-toggle ${isSelected ? 'selected' : ''}`}
            onClick={handleToggle}
            aria-pressed={isSelected}
            aria-label={`${isSelected ? 'Deselect' : 'Select'} suggestion ${meta.label}`}
          >
            <span className="analysis-selection-toggle__icon">{isSelected ? '✓' : '+'}</span>
            <span>{actionLabel}</span>
          </button>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="resume-analysis-results">
        <div className="analysis-header">
          <h3>📋 Resume Analysis</h3>
        </div>
        <div className="analysis-loading">
          <div className="spinner"></div>
          <p>Analyzing resume... This usually takes about 30-60 seconds.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="resume-analysis-results">
        <div className="analysis-header">
          <h3>📋 Resume Analysis</h3>
        </div>
        <div className="analysis-error">
          <p>⚠️ {error}</p>
        </div>
      </div>
    );
  }

  if (analysis.analysisStatus === 'processing') {
    return (
      <div className="resume-analysis-results">
        <div className="analysis-header">
          <h3>📋 Resume Analysis</h3>
        </div>
        <div className="analysis-loading">
          <div className="spinner"></div>
          <p>Analysis in progress... This usually takes about 30-60 seconds.</p>
        </div>
      </div>
    );
  }

  if (analysis.analysisStatus === 'failed') {
    return (
      <div className="resume-analysis-results">
        <div className="analysis-header">
          <h3>📋 Resume Analysis</h3>
        </div>
        <div className="analysis-error">
          <p>❌ Analysis failed. Please try again.</p>
        </div>
      </div>
    );
  }

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'critical': return 'severity-critical';
      case 'high': return 'severity-high';
      case 'medium': return 'severity-medium';
      case 'low': return 'severity-low';
      default: return 'severity-medium';
    }
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'high': return 'priority-high';
      case 'medium': return 'priority-medium';
      case 'low': return 'priority-low';
      default: return 'priority-medium';
    }
  };

  return (
    <div className="resume-analysis-results">
      <div className="analysis-header">
        <h3>📋 Resume Analysis Results</h3>
        {(onGenerateResume || onGenerateResumeAi) && (
          <div className="analysis-header-actions">
            {selectableSuggestions.length > 0 && (
              <span className="analysis-selection-count">
                {selectedSuggestionCount} selected
              </span>
            )}
            {onGenerateResumeAi && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => onGenerateResumeAi(selectedSuggestionIds)}
                disabled={!selectedSuggestionCount || isGeneratingResumeAi}
                title={combinedGenerationDisabledReason || undefined}
              >
                {isGeneratingResumeAi ? 'Rewriting...' : 'AI Rewrite (Apply All Selected)'}
              </button>
            )}
            {onGenerateResume && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  if (!selectedSuggestionCount) {
                    return;
                  }
                  const suggestionIdsForGeneration = exactApplyRequiresConcreteReplacements
                    ? selectedApplicableSuggestionIds
                    : selectedSuggestionIds;
                  if (!suggestionIdsForGeneration.length) {
                    setGenerationInlineError(
                      'None of the selected suggestions include a concrete Before/After change, so the exact DOCX patcher has nothing safe to apply. Use AI Rewrite, or select a suggestion with a concrete replacement.',
                    );
                    return;
                  }
                  setGenerationInlineError(null);
                  onGenerateResume(suggestionIdsForGeneration);
                }}
                disabled={!selectedSuggestionCount || isGeneratingResume}
                title={combinedGenerationDisabledReason || undefined}
              >
                {isGeneratingResume ? 'Generating...' : 'Generate New Resume Using Selected Suggestions'}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="analysis-content">
        {generationInlineError && (
          <div className="analysis-selection-toolbar__note" role="alert">
            {generationInlineError}
          </div>
        )}
        {selectableSuggestions.length > 0 && (
          <div className="analysis-selection-toolbar">
            <div className="analysis-selection-toolbar__copy">
              <strong>Choose the exact fixes to carry into the generated resume.</strong>
              <p>
                {exactApplyRequiresConcreteReplacements
                  ? 'Use the bold Apply To Resume button on each suggestion card below. Exact DOCX generation only patches concrete Before/After matches, while AI/PDF generation can also use guidance-based suggestions.'
                  : 'Use the bold Apply To Resume button on each suggestion card below. Every selected suggestion can be carried into the generated PDF resume through the AI rewrite flow.'}
              </p>
            </div>
            <div className="analysis-selection-toolbar__actions">
              <button type="button" className="btn btn-secondary" onClick={handleSelectAllSuggestions}>
                Select All
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleClearSelectedSuggestions}>
                Clear
              </button>
            </div>
          </div>
        )}
        {selectedReviewOnlyCount > 0 && exactApplyRequiresConcreteReplacements && (
          <p className="analysis-selection-toolbar__note">
            {selectedReviewOnlyCount} selected suggestion{selectedReviewOnlyCount === 1 ? '' : 's'} {selectedReviewOnlyCount === 1 ? 'requires' : 'require'} AI/PDF generation guidance and may be skipped by the exact DOCX patcher.
          </p>
        )}

        {/* ATS Errors */}
        {filteredAtsErrors && filteredAtsErrors.length > 0 && (
          <div className="analysis-section ats-errors">
            <h4>🚨 ATS Errors</h4>
            <ul>
              {filteredAtsErrors.map((error: AnalysisError, idx: number) => (
                <li
                  key={`ats-error-${idx}`}
                  className={`${getSeverityColor(error.severity || 'medium')} ${selectedSuggestionIds.includes(`atsErrors:${idx}`) ? 'analysis-item-selected' : ''}`.trim()}
                >
                  {renderSuggestionSelectionControl('atsErrors', idx)}
                  <div className="error-header">
                    <span className="error-type">
                      {error.type ? error.type.replace('ats_', '').replace('_', ' ') : 'ATS Error'}
                    </span>
                    <span className={`severity-badge ${getSeverityColor(error.severity || 'medium')}`}>
                      {error.severity || 'medium'}
                    </span>
                  </div>
                  <p className="error-description">{error.description || 'No description available'}</p>
                  {error.suggestion && (
                    <p className="error-suggestion">💡 {error.suggestion}</p>
                  )}
                  {error.location && (
                    <p className="error-location">📍 {error.location}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Data Validation Errors */}
        {filteredDataValidationErrors && filteredDataValidationErrors.length > 0 && (
          <div className="analysis-section data-validation-errors">
            <h4>🔍 Data Validation Errors</h4>
            <ul>
              {filteredDataValidationErrors.map((error: DataValidationError, idx: number) => (
                <li
                  key={`data-error-${idx}`}
                  className={selectedSuggestionIds.includes(`dataValidationErrors:${idx}`) ? 'analysis-item-selected' : undefined}
                >
                  {renderSuggestionSelectionControl('dataValidationErrors', idx)}
                  <div className="error-header">
                    <span className="error-type">{error.type || 'Validation Error'}</span>
                    {error.field && (
                      <span className="error-field">{error.field}</span>
                    )}
                  </div>
                  <p className="error-description">{error.description || 'No description available'}</p>
                  <div className="error-values">
                    {error.currentValue && (
                      <span className="current-value">Current: {error.currentValue}</span>
                    )}
                    {error.suggestedValue && (
                      <span className="suggested-value">→ Suggested: {error.suggestedValue}</span>
                    )}
                  </div>
                  {error.location && (
                    <p className="error-location">📍 {error.location}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Discrepancies */}
        {filteredDiscrepancies && filteredDiscrepancies.length > 0 && (
          <div className="analysis-section discrepancies">
            <h4>⚠️ Discrepancies</h4>
            <ul>
              {filteredDiscrepancies.map((discrepancy: Discrepancy, idx: number) => (
                <li key={`discrepancy-${idx}`}>
                  <p className="discrepancy-description">{discrepancy.description}</p>
                  {discrepancy.affectedSections && discrepancy.affectedSections.length > 0 && (
                    <p className="affected-sections">
                      Affected sections: {discrepancy.affectedSections.join(', ')}
                    </p>
                  )}
                  {discrepancy.suggestion && (
                    <p className="discrepancy-suggestion">💡 {discrepancy.suggestion}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Employment Gaps */}
        {analysis.employmentGaps && Array.isArray(analysis.employmentGaps) && analysis.employmentGaps.length > 0 && (
          <div className="analysis-section employment-gaps">
            <h4>📅 Employment Gaps</h4>
            <ul>
              {analysis.employmentGaps.map((gap: EmploymentGap, idx: number) => (
                <li key={`gap-${idx}`}>
                  <div className="gap-header">
                    {gap.duration && (
                      <span className="gap-duration">{gap.duration}</span>
                    )}
                    {gap.startDate && gap.endDate && (
                      <span className="gap-dates">{gap.startDate} - {gap.endDate}</span>
                    )}
                  </div>
                  {gap.description && (
                    <p className="gap-description">{gap.description}</p>
                  )}
                  {gap.suggestions && gap.suggestions.length > 0 && (
                    <ul className="gap-suggestions">
                      {gap.suggestions.map((suggestion: string, sIdx: number) => (
                        <li key={`gap-suggestion-${sIdx}`}>💡 {suggestion}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Job Hopping Patterns */}
        {analysis.jobHoppingPatterns && analysis.jobHoppingPatterns.length > 0 && (
          <div className="analysis-section job-hopping">
            <h4>🔄 Job Hopping Patterns</h4>
            <ul>
              {analysis.jobHoppingPatterns.map((pattern: JobHoppingPattern, idx: number) => (
                <li key={`pattern-${idx}`}>
                  {pattern.pattern && (
                    <p className="pattern-description">{pattern.pattern}</p>
                  )}
                  {pattern.jobs && pattern.jobs.length > 0 && (
                    <div className="pattern-jobs">
                      <h5>Jobs in Pattern:</h5>
                      <ul>
                        {pattern.jobs.map((job: any, jIdx: number) => (
                          <li key={`job-${jIdx}`}>
                            {job?.position || 'Unknown'} at {job?.company || 'Unknown'} {job?.duration && `(${job.duration})`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {pattern.suggestions && pattern.suggestions.length > 0 && (
                    <ul className="pattern-suggestions">
                      {pattern.suggestions.map((suggestion: string, sIdx: number) => (
                        <li key={`pattern-suggestion-${sIdx}`}>💡 {suggestion}</li>
                      ))}
                    </ul>
                  )}
                  {pattern.recommendedAction && (
                    <p className="recommended-action">
                      ✅ Recommended: {pattern.recommendedAction ? (typeof pattern.recommendedAction === 'string' ? pattern.recommendedAction.replace('_', ' ') : pattern.recommendedAction) : 'N/A'}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Grammar Errors */}
        {filteredGrammarErrors && filteredGrammarErrors.length > 0 && (
          <div className="analysis-section grammar-errors">
            <h4>✏️ Grammar & Syntax Errors</h4>
            <ul>
              {filteredGrammarErrors.map((error: GrammarError, idx: number) => (
                <li
                  key={`grammar-${idx}`}
                  className={selectedSuggestionIds.includes(`grammarErrors:${idx}`) ? 'analysis-item-selected' : undefined}
                >
                  {renderSuggestionSelectionControl('grammarErrors', idx)}
                  <div className="grammar-correction">
                    <span className="grammar-original">"{error.original || 'N/A'}"</span>
                    <span className="grammar-arrow">→</span>
                    <span className="grammar-corrected">"{error.corrected || 'N/A'}"</span>
                  </div>
                  {error.description && (
                    <p className="grammar-description">{error.description}</p>
                  )}
                  {error.location && (
                    <p className="grammar-location">📍 {error.location}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Professional Improvements */}
        {filteredProfessionalImprovements && filteredProfessionalImprovements.length > 0 && (
          <div className="analysis-section professional-improvements">
            <h4>💼 Professional Improvements</h4>
            <ul>
              {filteredProfessionalImprovements.map((improvement: Improvement, idx: number) => (
                <li
                  key={`prof-${idx}`}
                  className={`${getPriorityColor(improvement.priority || 'medium')} ${selectedSuggestionIds.includes(`professionalImprovements:${idx}`) ? 'analysis-item-selected' : ''}`.trim()}
                >
                  {renderSuggestionSelectionControl('professionalImprovements', idx)}
                  {(() => {
                    const safeAfter = buildSafeReplacementTarget(improvement.before, improvement.after);
                    return (
                      <>
                  <div className="improvement-header">
                    <span className="improvement-category">{improvement.category || 'General'}</span>
                    <span className={`priority-badge ${getPriorityColor(improvement.priority || 'medium')}`}>
                      {improvement.priority || 'medium'}
                    </span>
                  </div>
                  <p className="improvement-description">{improvement.description || 'No description available'}</p>
                  {improvement.suggestion && (
                    <p className="improvement-suggestion">💡 {improvement.suggestion}</p>
                  )}
                  {improvement.before && improvement.after && (
                    <div className="improvement-comparison">
                      <div className="before">
                        <strong>Before:</strong> {improvement.before}
                      </div>
                      <div className="after">
                        <strong>After:</strong> {safeAfter || improvement.after}
                      </div>
                    </div>
                  )}
                      </>
                    );
                  })()}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Field-Specific Suggestions */}
        {filteredFieldSpecificSuggestions && filteredFieldSpecificSuggestions.length > 0 && (
          <div className="analysis-section field-suggestions">
            <h4>🎯 Field-Specific Suggestions</h4>
            <ul>
              {filteredFieldSpecificSuggestions.map((improvement: Improvement, idx: number) => (
                <li
                  key={`field-${idx}`}
                  className={`${getPriorityColor(improvement.priority || 'medium')} ${selectedSuggestionIds.includes(`fieldSpecificSuggestions:${idx}`) ? 'analysis-item-selected' : ''}`.trim()}
                >
                  {renderSuggestionSelectionControl('fieldSpecificSuggestions', idx)}
                  <div className="improvement-header">
                    <span className="improvement-category">{improvement.category || 'General'}</span>
                    <span className={`priority-badge ${getPriorityColor(improvement.priority || 'medium')}`}>
                      {improvement.priority || 'medium'}
                    </span>
                  </div>
                  <p className="improvement-description">{improvement.description || 'No description available'}</p>
                  {improvement.suggestion && (
                    <p className="improvement-suggestion">💡 {improvement.suggestion}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Contextual Improvements */}
        {filteredContextualImprovements && filteredContextualImprovements.length > 0 && (
          <div className="analysis-section contextual-improvements">
            <h4>🔧 Contextual Improvements</h4>
            <ul>
              {filteredContextualImprovements.map((improvement: Improvement, idx: number) => (
                <li
                  key={`context-${idx}`}
                  className={`${getPriorityColor(improvement.priority || 'medium')} ${selectedSuggestionIds.includes(`contextualImprovements:${idx}`) ? 'analysis-item-selected' : ''}`.trim()}
                >
                  {renderSuggestionSelectionControl('contextualImprovements', idx)}
                  <div className="improvement-header">
                    <span className="improvement-category">{improvement.category || 'General'}</span>
                    <span className={`priority-badge ${getPriorityColor(improvement.priority || 'medium')}`}>
                      {improvement.priority || 'medium'}
                    </span>
                  </div>
                  <p className="improvement-description">{improvement.description || 'No description available'}</p>
                  {improvement.suggestion && (
                    <p className="improvement-suggestion">💡 {improvement.suggestion}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Other Suggestions */}
        {filteredOtherSuggestions && filteredOtherSuggestions.length > 0 && (
          <div className="analysis-section other-suggestions">
            <h4>💡 Other Suggestions</h4>
            <ul>
              {filteredOtherSuggestions.map((improvement: Improvement, idx: number) => (
                <li
                  key={`other-${idx}`}
                  className={`${getPriorityColor(improvement.priority || 'medium')} ${selectedSuggestionIds.includes(`otherSuggestions:${idx}`) ? 'analysis-item-selected' : ''}`.trim()}
                >
                  {renderSuggestionSelectionControl('otherSuggestions', idx)}
                  <div className="improvement-header">
                    <span className="improvement-category">{improvement.category || 'General'}</span>
                    <span className={`priority-badge ${getPriorityColor(improvement.priority || 'medium')}`}>
                      {improvement.priority || 'medium'}
                    </span>
                  </div>
                  <p className="improvement-description">{improvement.description || 'No description available'}</p>
                  {improvement.suggestion && (
                    <p className="improvement-suggestion">💡 {improvement.suggestion}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Rewrite Optimization Suggestions */}
        {filteredRewriteOptimizationSuggestions && filteredRewriteOptimizationSuggestions.length > 0 && (
          <div className="analysis-section rewrite-optimization-suggestions">
            <h4>✏️ Rewrite Optimization Suggestions</h4>
            <p className="section-description">
              Specific suggestions based on resume rewrite principles to improve structure, content, and impact.
            </p>
            <div className="suggestions-by-category">
              {['structural', 'content', 'skills', 'consistency', 'impact', 'technical', 'narrative'].map((category) => {
                const categorySuggestions = filteredRewriteOptimizationSuggestions.filter(
                  (suggestion: RewriteOptimizationSuggestion) => suggestion.category === category
                );
                if (categorySuggestions.length === 0) return null;
                
                const categoryLabels: Record<string, string> = {
                  structural: '📐 Structural',
                  content: '📝 Content',
                  skills: '🛠️ Skills',
                  consistency: '🔄 Consistency',
                  impact: '🎯 Impact',
                  technical: '⚙️ Technical',
                  narrative: '📖 Narrative'
                };
                
                return (
                  <div key={category} className="category-group">
                    <h5>{categoryLabels[category] || category}</h5>
                    <ul>
                      {filteredRewriteOptimizationSuggestions
                        .map((suggestion: RewriteOptimizationSuggestion, originalIndex: number) => ({ suggestion, originalIndex }))
                        .filter(({ suggestion }) => suggestion.category === category)
                        .map(({ suggestion, originalIndex }, idx: number) => (
                        <li
                          key={`rewrite-${category}-${idx}`}
                          className={`${getPriorityColor(suggestion.priority || 'medium')} ${selectedSuggestionIds.includes(`rewriteOptimizationSuggestions:${originalIndex}`) ? 'analysis-item-selected' : ''}`.trim()}
                        >
                          {renderSuggestionSelectionControl('rewriteOptimizationSuggestions', originalIndex)}
                          {(() => {
                            const safeAfter = buildSafeReplacementTarget(suggestion.before, suggestion.after);
                            return (
                              <>
                          <div className="suggestion-header">
                            <span className={`priority-badge ${getPriorityColor(suggestion.priority || 'medium')}`}>
                              {suggestion.priority || 'medium'}
                            </span>
                            {suggestion.location && (
                              <span className="suggestion-location">📍 {suggestion.location}</span>
                            )}
                          </div>
                          <p className="suggestion-description">{suggestion.description || 'No description available'}</p>
                          {suggestion.suggestion && (
                            <p className="suggestion-text">💡 {suggestion.suggestion}</p>
                          )}
                          {suggestion.before && suggestion.after && (
                            <div className="before-after">
                              <div className="before">
                                <strong>Before:</strong> <span className="text-muted">{suggestion.before}</span>
                              </div>
                              <div className="arrow">→</div>
                              <div className="after">
                                <strong>After:</strong> <span className="text-success">{safeAfter || suggestion.after}</span>
                              </div>
                            </div>
                          )}
                              </>
                            );
                          })()}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="analysis-summary">
          <h4>📊 Analysis Summary</h4>
          <div className="summary-stats">
            {filteredAtsErrors && filteredAtsErrors.length > 0 && (
              <div className="stat-item">
                <span className="stat-label">ATS Errors:</span>
                <span className="stat-value">{filteredAtsErrors.length}</span>
              </div>
            )}
            {filteredDataValidationErrors && filteredDataValidationErrors.length > 0 && (
              <div className="stat-item">
                <span className="stat-label">Data Errors:</span>
                <span className="stat-value">{filteredDataValidationErrors.length}</span>
              </div>
            )}
            {filteredGrammarErrors && filteredGrammarErrors.length > 0 && (
              <div className="stat-item">
                <span className="stat-label">Grammar Errors:</span>
                <span className="stat-value">{filteredGrammarErrors.length}</span>
              </div>
            )}
            {(analysis.employmentGaps && analysis.employmentGaps.length > 0) && (
              <div className="stat-item">
                <span className="stat-label">Employment Gaps:</span>
                <span className="stat-value">{analysis.employmentGaps.length}</span>
              </div>
            )}
            {(filteredProfessionalImprovements && filteredProfessionalImprovements.length > 0) && (
              <div className="stat-item">
                <span className="stat-label">Improvements:</span>
                <span className="stat-value">{filteredProfessionalImprovements.length}</span>
              </div>
            )}
            {filteredRewriteOptimizationSuggestions && filteredRewriteOptimizationSuggestions.length > 0 && (
              <div className="stat-item">
                <span className="stat-label">Rewrite Suggestions:</span>
                <span className="stat-value">{filteredRewriteOptimizationSuggestions.length}</span>
              </div>
            )}
          </div>
        </div>

        {/* No Data Message */}
        {!filteredAtsErrors?.length && 
         !filteredDataValidationErrors?.length && 
         !filteredDiscrepancies?.length && 
         !(analysis.employmentGaps && analysis.employmentGaps.length > 0) &&
         !analysis.jobHoppingPatterns?.length &&
         !filteredGrammarErrors?.length &&
         !filteredProfessionalImprovements?.length &&
         !filteredFieldSpecificSuggestions?.length &&
         !filteredContextualImprovements?.length &&
         !filteredOtherSuggestions?.length &&
         !filteredRewriteOptimizationSuggestions?.length && (
          <div className="analysis-empty-state">
            <p>✅ No issues or improvements found in this resume analysis.</p>
            <p className="empty-state-subtitle">Your resume looks good!</p>
          </div>
        )}
      </div>
    </div>
  );
};
